import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Typography } from "@mui/material";
import { useGetAll } from "@mb-pro-ui/utils";
import { useEffect, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "wouter";
import { related, type Cdec, type LookupWithDescription } from "../api/types";
import { isProfileMenuVisible, useCustomerHub } from "../api/useCustomer";
import { canReadCustomerDatasheet, useMe } from "../api/useMe";
import { formatAgo, formatDateTime, formatEventTime, isTemporarilyInactive } from "../components/format";
import PasswordValue from "../components/PasswordRow";
import { DenseList, Row } from "../components/Rows";
import StateBadge from "../components/StateBadge";
import { FullPageSpinner, Notice } from "../components/States";
import TopBar from "../components/TopBar";
import { useActiveOrg } from "../org/OrgContext";
import { useRecentCustomers } from "../recentCustomers";

function Detail({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", gap: 1, py: 0.5, borderBottom: 1, borderColor: "divider", alignItems: "baseline" }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 88, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>{children}</Box>
    </Box>
  );
}

const lookupText = (value: LookupWithDescription | string | null | undefined) => {
  const item = related(value);
  return item?.["localized-description"] ?? item?.description ?? "";
};

function useLatestEvent(customerId: string) {
  return useGetAll<Cdec>("alarm/cdec-all", {
    filter: { customer: { eq: customerId } },
    sort: ["-id"],
    page: { limit: 1 },
    fields: { "cdec-all": ["arrived", "event-code", "localized-description"] },
    refetchInterval: 30 * 1000,
  });
}

export default function CustomerHub() {
  const intl = useIntl();
  const { id = "" } = useParams<{ id: string }>();
  const org = useActiveOrg();
  const { data: me } = useMe();
  const datasheet = canReadCustomerDatasheet(me);
  const { data: customer, isLoading, isError } = useCustomerHub(id, datasheet);
  const latest = useLatestEvent(id);
  const { remember } = useRecentCustomers(org.segment);

  const account = customer?.account;
  useEffect(() => {
    if (customer && account) {
      remember({
        id: customer.id,
        account,
        name: customer.name ?? null,
        address: customer.address ?? null,
      });
    }
    // Remember once per loaded customer, not on every recent-list change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, account]);

  if (isLoading) {
    return (
      <>
        <TopBar title="…" back="/" />
        <FullPageSpinner />
      </>
    );
  }

  if (isError || !customer) {
    return (
      <>
        <TopBar title={<FormattedMessage defaultMessage="Customer" />} back="/" />
        <Notice>
          <FormattedMessage defaultMessage="The customer could not be loaded." />
        </Notice>
      </>
    );
  }

  const tempInactive = isTemporarilyInactive(customer["temp-inactive-ts"]);
  const latestEvent = latest.data?.[0];
  const showDatasheet = (menu: string, item: string) =>
    datasheet && isProfileMenuVisible(customer, menu, item);
  const count = (ids: string[] | undefined) =>
    ids ? <Typography variant="caption" color="text.secondary">{ids.length}</Typography> : null;

  return (
    <>
      <TopBar title={`${customer.account ?? ""} · ${customer.name ?? ""}`} back="/" />
      <Box sx={{ px: 1.5, pt: 0.5, pb: 1, bgcolor: "background.paper" }}>
        <Detail label={<FormattedMessage defaultMessage="Account" />}>
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
            {customer.account}
          </Typography>
        </Detail>
        <Detail label={<FormattedMessage defaultMessage="Name" />}>
          <Typography variant="body2">{customer.name}</Typography>
        </Detail>
        <Detail label={<FormattedMessage defaultMessage="Address" />}>
          <Typography variant="body2">{customer.address}</Typography>
        </Detail>
        <Detail label={<FormattedMessage defaultMessage="State" />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {customer.active === false || tempInactive ? (
              <StateBadge active={customer.active} tempInactiveTs={customer["temp-inactive-ts"]} />
            ) : (
              <Typography variant="body2">
                <FormattedMessage defaultMessage="Active" />
              </Typography>
            )}
            {customer.active !== false && tempInactive && (
              <Typography variant="caption">
                <FormattedMessage
                  defaultMessage="until {time}"
                  values={{ time: formatDateTime(intl, customer["temp-inactive-ts"]) }}
                />
              </Typography>
            )}
          </Box>
        </Detail>
        {(customer.group || customer["object-type"]) && (
          <Detail label={<FormattedMessage defaultMessage="Group / type" />}>
            <Typography variant="body2">
              {[lookupText(customer.group), lookupText(customer["object-type"])].filter(Boolean).join(" · ")}
            </Typography>
          </Detail>
        )}
        <Detail label={<FormattedMessage defaultMessage="Last device message" />}>
          <Typography variant="body2">
            {customer["last-event-ts"] ? formatAgo(intl, customer["last-event-ts"]) : "–"}
          </Typography>
        </Detail>
        {"passwd" in customer && (
          <Detail label={<FormattedMessage defaultMessage="Password" />}>
            <PasswordValue value={customer.passwd ?? null} />
          </Detail>
        )}
      </Box>

      <DenseList>
        <Row
          href={`/c/${id}/events`}
          primary={<FormattedMessage defaultMessage="Events" />}
          secondary={
            latestEvent
              ? `${formatEventTime(intl, latestEvent.arrived)} ${latestEvent["event-code"] ?? ""} ${latestEvent["localized-description"] ?? ""}`
              : undefined
          }
          end={<ChevronRightIcon color="action" />}
        />
        {showDatasheet("configuration", "zones") && (
          <Row
            href={`/c/${id}/zones`}
            primary={<FormattedMessage defaultMessage="Zones" />}
            end={<Box sx={{ display: "flex", alignItems: "center" }}>{count(customer.zones)}<ChevronRightIcon color="action" /></Box>}
          />
        )}
        {showDatasheet("configuration", "partitions") && (
          <Row
            href={`/c/${id}/partitions`}
            primary={<FormattedMessage defaultMessage="Partitions" />}
            end={<Box sx={{ display: "flex", alignItems: "center" }}>{count(customer.partitions)}<ChevronRightIcon color="action" /></Box>}
          />
        )}
        {showDatasheet("configuration", "users") && (
          <Row
            href={`/c/${id}/users`}
            primary={<FormattedMessage defaultMessage="Users" />}
            end={<Box sx={{ display: "flex", alignItems: "center" }}>{count(customer.users)}<ChevronRightIcon color="action" /></Box>}
          />
        )}
        {showDatasheet("interventions", "notifiables") && (
          <Row
            href={`/c/${id}/notifiables`}
            primary={<FormattedMessage defaultMessage="Notifiable persons" />}
            end={<Box sx={{ display: "flex", alignItems: "center" }}>{count(customer["notifiable-persons"])}<ChevronRightIcon color="action" /></Box>}
          />
        )}
      </DenseList>
    </>
  );
}
